import { GameState } from "./game-state";
import { Ball } from "./ball";
import { Player } from "./player";
import { HitResultEnum } from "../types/hit-result-enum";
import { Game } from "./game";
import { BallTypeEnum } from "../types/ball-type-enum";
import { GameConfig } from "../config/game-config";
import { IAssetsConfig } from "../types/game-config-type";
import gameApplication from "./game-app";
import { MutationType } from "../stores/mutation-types";
import { BallsFactory } from "./balls-factory";
import { store } from "../stores";
import { Vector2 } from "../math/vector2";

const sounds: IAssetsConfig = GameConfig.sounds;

enum TableState {
    Opened, Closed
}
export class Game8Pool extends Game {

  private solidIcon: string = '1.svg';
  private stripesIcon: string = '15.sv';
  private blackIcon: string = '8.svg';
  private unknownIcon: string = 'default-pool.svg';

  private eightBall: Ball;
  private breakShotFlag: boolean;
  private state: TableState;

  constructor() {
    super();
    this.initGame();
  }

  /**
   * This initilises the game. 
   */
  public initGame(): void {
    this.state = TableState.Opened;

    this.breakShotFlag = true;

    this.rackBalls();

    // initilise the game state
    this.gameState = new GameState();
    this.gameState.setupCueBall = true;

    // start the game play
    this.startGame();
  }

  private rackBalls(): void {
    this.gameBalls = BallsFactory.createPool8Balls();
    this.cueBall = this.gameBalls[0];
    this.eightBall = this.gameBalls[8];
  }

  protected processEvents(): HitResultEnum {
    // Check if black 8 ball is potted.
    if (!this.eightBall.onTable && this.cueBall.onTable) {
      if (this.currentTurnPlayer.ballType !== BallTypeEnum.Eight) {
        // if player scratched black 8 ball before other balls from own group
        if (this.breakShotFlag) {
          store.commit(MutationType.GAME_MESSAGE,`RESULT: 8 Ball ${gameApplication.gameItem.gameType} - ${this.currentTurnPlayer.playerId} wins to ${this.nextTurnPlayer.playerId} - GOLDEN BREAK.`);
          return HitResultEnum.WIN_GAME_GOLDEN_BREAK;
        }
        else {
          let n: number = (this.currentTurnPlayerIndex == 0 ? 1 : 0);
          store.commit(MutationType.GAME_MESSAGE, `RESULT: 8 Ball ${gameApplication.gameItem.gameType} - ${this.currentTurnPlayer.playerId} loses to ${this.nextTurnPlayer.playerId} by a foul on the black.`);
          return HitResultEnum.LOST_GAME;
        }
      }
      else {
        // see if all of the opponents balls are on the table
        let ballCount: number = 0;

        for (let i: number = 1; i <= 15; i++) {
          const b: Ball = this.balls[i];
          if (b.onTable) {
            // the ball is not the cue ball
            if (b.type !== BallTypeEnum.Cue) {
              ballCount++;
            }
          }
        }

        // all of the opponents balls are on the table
        if (ballCount == 7) {
          store.commit(MutationType.GAME_MESSAGE, `RESULT: 8 Ball ${gameApplication.gameItem.gameType} - ${this.currentTurnPlayer.playerId} wins to ${this.nextTurnPlayer.playerId} - SEVEN BALLING.`);
          return HitResultEnum.WIN_GAME_SEVEN_BALLING;
        }

        store.commit(MutationType.GAME_MESSAGE, `RESULT: 8 Ball ${gameApplication.gameItem.gameType} - ${this.currentTurnPlayer.playerId} wins to ${this.nextTurnPlayer.playerId}.`);
        return HitResultEnum.WIN_GAME;
      }
    }
  
    // check if cue ball is potted
    if (!this.cueBall.onTable) {
      this.breakShotFlag = false;
      if (this.eightBall.onTable) {
        store.commit(MutationType.GAME_MESSAGE, `Player ${this.currentTurnPlayer.playerId} scratched cue ball (foul).`);
        this.isFoul = true; // opponent can set cue ball position
        return HitResultEnum.PLAYER_CHANGE;
      }
      else {
        // Pocketing the cue ball in last hit - game over
        store.commit(MutationType.GAME_MESSAGE, `RESULT: 8 Ball ${this.currentTurnPlayer.playerId} - ${this.currentTurnPlayer.playerId} loses to ${this.nextTurnPlayer.playerId} by scratching cue ball with black ball.`);
        let n: number = (this.currentTurnPlayerIndex == 0 ? 1 : 0);
        return HitResultEnum.LOST_GAME;
      }
    }

    if (!this.gameState.ballBallCollision) {
      this.breakShotFlag = false;
      store.commit(MutationType.GAME_MESSAGE, `Player ${this.currentTurnPlayer.playerId} hit no ball (foul).`);
      this.isFoul = true; // opponent can set cue ball position
      return HitResultEnum.PLAYER_CHANGE;
    }

    if (this.state == TableState.Opened) {
      if (this.breakShotFlag) {
        this.breakShotFlag = false;
        if (this.gameState.pocketedBalls.length > 0) {
          store.commit(MutationType.GAME_MESSAGE, `${this.currentTurnPlayer.playerId} potted on break, table is still open.`);
          return HitResultEnum.OK;
        }

        if (this.gameState.ballRailCollision) {
          // so we have valid break shot, but no balls scratched - so switch to
          // opponent table remains open, cue ball on same position
          return HitResultEnum.PLAYER_CHANGE;
        }
        else {
          // there is invalid break shot - no ball is sunk and no ball had contact with rail
          store.commit(MutationType.GAME_MESSAGE, `${this.currentTurnPlayer.playerId} had no ball contact with the rail after break shot (foul).`);
          this.isFoul = true; // opponent can set cue ball position
          return HitResultEnum.PLAYER_CHANGE;
        }
      }
      else {
        // any hit after break shot, table is still open
        if (this.gameState.pocketedBalls.length > 0) {
          // set players ball type to first pocketed ball's type
          this.closeTable(this.gameState.pocketedBalls[0].type);
          return HitResultEnum.OK;
        }

        if (this.gameState.ballRailCollision) {
          // so we have a valid shot, but no balls scratched - so switch to
          // opponent table cue ball position same as before
          return HitResultEnum.PLAYER_CHANGE;
        }
        else {
          // there is invalid shot - no ball is sunk and no ball had contact with rails
          store.commit(MutationType.GAME_MESSAGE, `${this.currentTurnPlayer.playerId} had no ball contact with the rail after hitting the object ball (foul).`);
          this.isFoul = true; // opponent can set cue ball position	
          return HitResultEnum.PLAYER_CHANGE;
        }
      }
    }
    else { // here we are in play mode
      // If either player has no balls on the table then the player in on the 8 ball. 
      this.checkIfPlayerHasBalls(this.currentTurnPlayer);
      this.checkIfPlayerHasBalls(this.nextTurnPlayer);

      // check if cue ball had contact with own ball first
      const playerBallType: BallTypeEnum = this.currentTurnPlayer.ballType;

      if (this.gameState.firstCollidedBall.type !== playerBallType) {
        store.commit(MutationType.GAME_MESSAGE, `${this.currentTurnPlayer.playerId} hit wrong ball (foul).`);
        this.isFoul = true; // opponent can set cue ball position			
        return HitResultEnum.PLAYER_CHANGE;
      }
    
      for (const b of this.gameState.pocketedBalls) {
        if (b.type !== playerBallType) {
          store.commit(MutationType.GAME_MESSAGE, `${this.currentTurnPlayer.playerId} potted wrong ball (foul).`);
          this.isFoul = true; // opponent can set cue ball position
          return HitResultEnum.PLAYER_CHANGE;
        }
      }

      if (this.gameState.pocketedBalls.length > 0) {
        return HitResultEnum.OK;
      }

      if (this.gameState.ballRailCollision) {
        // so we have a valid shot, but no balls scratched - so switch to opponent
        // table cue ball position same as before
        return HitResultEnum.PLAYER_CHANGE;
      }
      else {
        // invalid shot because no ball is sunk and no ball had contact with rails
        store.commit(MutationType.GAME_MESSAGE, `${this.currentTurnPlayer.playerId} had no ball contact with the rail after hitting the object ball (foul).`);
        this.isFoul = true; // opponent can set cue ball position
        return HitResultEnum.PLAYER_CHANGE;
      }
    }
  }

  private checkIfPlayerHasBalls(p: Player): void {
    const playerBallType: BallTypeEnum = p.ballType;

    if (playerBallType !== BallTypeEnum.Eight) {
      const sx: number = playerBallType == BallTypeEnum.Solid ? 1 : 9;
      let change: boolean = true;
      for (let i: number = sx; i < sx + 7; i++) {
        let b: Ball = this.balls[i];
        if (b.onTable) {
          change = false;
          break;
        }
      }
      if (change) {
        this.setPlayerBallType(p, BallTypeEnum.Eight);
      }
    }
  }
 
  private setPlayerBallType(p: Player, ballType: BallTypeEnum): void {
    let icon: string = "";

    switch (ballType) {
      case BallTypeEnum.Solid:
        icon = this.solidIcon;
        break;
      case BallTypeEnum.Stripe:
        icon = this.stripesIcon;
        break;
      case BallTypeEnum.Eight:
        icon = this.blackIcon;
        break;
      default:    
    }

    p.ballType = ballType;
    p.player.ball = icon;
  }
  
  private closeTable(ballType: BallTypeEnum): void {
    this.setPlayerBallType(this.currentTurnPlayer, ballType);
    this.setPlayerBallType(this.nextTurnPlayer, ballType == BallTypeEnum.Solid ? BallTypeEnum.Stripe : BallTypeEnum.Solid);   
    this.state = TableState.Closed;
    store.commit(MutationType.GAME_MESSAGE, `The table is closed.`);
  }

  /**
   * Validate the position of the cue ball on the table.
   * 
   * @param position - the position of the cue ball
	 * @returns Retruns "true" if the cue ball postion is valid and "false" of not
   */
  public validateCueBallPosition(position: Vector2): boolean {
		if (this.state == TableState.Opened) {
			if (position.x > Game.BAULK_LINE_POS) {
				return false;
			}
		}
		return true;
	}

  /**
   * This handles the change the of the current player if necessary.
   */
  public continueGame(): void {
    if (this.breakShotFlag &&
      this.currentTurnPlayer.player !== undefined &&
      this.hitResult === undefined) {
      this.currentTurnPlayer.player.ball = 'default-pool.svg';
      this.nextTurnPlayer.player.ball = 'default-pool.svg';
    }

    switch (this.hitResult) {
      case HitResultEnum.PLAYER_CHANGE:
        this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.players.length;

        // Store the player change in the Vues store.
        store.commit(MutationType.PLAY_TURN, this.players[this.currentPlayerIndex].playerId);

        // Emit the "player change" event to the Vue.js front end.
        gameApplication.fireEvent('PLAYER_CHANGE');

        break;
      case HitResultEnum.OK:
        // Store the current player in the Vues store.
        // FIXME: I think here there is no change in the player turn so this is not needed.
        store.commit(MutationType.PLAY_TURN, this.players[this.currentPlayerIndex].playerId);

        // Emit the "Okay" event to the Vue.js front end.
        gameApplication.fireEvent('OK');

        break;
    }

    // Store the players updated player details in the Vuex.
    // This this updates the status of the players in the front end.
    store.commit(MutationType.PLAYER1, this.players[0]);
    store.commit(MutationType.PLAYER2, this.players[1]);
  }
}

